import equals from 'ramda/es/equals';
import { ViewType, StudyCriteriaCategory, SeriesCriteriaCategory } from 'generated/graphql';
import type { HangingProtocolPayload } from 'generated/graphql';
import type {
  StepConfiguration,
  ViewportConfigurationEntry,
} from '../../../../HangingProtocol/steppedHPTypes';
import {
  stepToGqlInput,
  isEmptyViewport,
  parseLayoutString,
  isViewportInLayoutBounds,
} from '../../../../HangingProtocol/steppedHPTypes';
import {
  assignMirrorGroupIds,
  hasNoResolvedSeries,
  type ViewportSeriesIds,
} from '../utils/viewportsConfigToHangingProtocol';
import { stepViewportToFormViewport } from '../../../../HangingProtocol/hooks/useStepFormSync';
import { generateViewportId, viewportIdToConfig } from '../../../viewerUtils';
import { logger } from 'modules/logger';
import type { Layouts } from '../../../StudyLoader/viewerLoaderState';
import type { SubmitFormIntent, ClassificationState } from '../constants';
import { CLASSIFICATION_STATE, SUBMIT_FORM_INTENT } from '../constants';

// Extended type that includes both ontology_id and display_name for UI display
export type ExtendedStudyCriteria = {
  category: StudyCriteriaCategory;
  value: string; // ontology_id
  displayName: string; // display_name for UI
};

// Extended type for series criteria that includes both ontology_id and display_name for UI display
export type ExtendedSeriesCriteria = {
  category: SeriesCriteriaCategory;
  value: string; // ontology_id
  displayName: string; // display_name for UI
};

export type HangingProtocolFormState = {
  hangingProtocolName: string;
  studyCriteriaSourceText: string;
  studyClassification: Array<ExtendedStudyCriteria>;
  ontologyVersion: string;
  submitFormIntent: SubmitFormIntent;
  /**
   * Discriminator identifying which HP this form state belongs to: the HP smid
   * in UPDATE mode, or null in CREATE mode. Used by the cross-window merge fn to
   * tell a same-HP broadcast race (preserve cross-window viewports) from a
   * case/HP switch (drop stale viewports). Optional so legacy writers that don't
   * set it behave exactly as before.
   */
  hpSmid?: Nullable<string>;
  /** Last completed form-state initialization, shared so peer classifiers can replay cache hits. */
  initializationVersion?: number;
  /**
   * Set only on the value a window broadcasts when its form actually closes.
   *
   * Exists because a genuine close and "this window simply has no form open" are byte-identical
   * otherwise -- both are `defaultHPFormState` -- and the two need opposite handling: a close must
   * clear every window, while a bare default from a newly connected window must not touch a form
   * the user is editing. Optional, so a writer that never sets it keeps the old meaning of "no form
   * content", which is what the receiver assumes when it is absent.
   */
  closeSignal?: boolean;
  viewports: {
    [viewportId: string]: {
      seriesCriteriaSourceText: string;
      comparisonIndex: number | null;
      seriesClassificationCriteria?: Array<ExtendedSeriesCriteria>; // Optional - omitted during init, re-fetched from server
      /**
       * Signature of the classifier inputs that produced `seriesClassificationCriteria`.
       *
       * Recorded by whoever supplies the criteria, and compared against the viewport's live inputs to
       * tell settled from stale. This is why no other writer has to remember anything: change the
       * description, the study description or the comparison index and the signatures stop matching on
       * their own. Absent means nothing has been classified yet.
       */
      criteriaSignature?: string;
      /**
       * Signature of the classifier inputs whose query failed, if the last attempt failed.
       *
       * Kept separate from `criteriaSignature` so a failure is attributed to the exact inputs that
       * failed. Edit the description after a failure and this stops matching too, so the viewport goes
       * back to waiting rather than staying stuck on a stale error.
       */
      failedSignature?: string;
      isClearViewport: boolean;
      viewType: ViewType;
      /**
       * Which series the user actually dropped here, if they dropped one this session.
       *
       * Session-only and never persisted -- hanging protocols are stored criteria-only, and this is
       * not a criterion. It exists because everything else the form records about a viewport is a
       * *matching rule*, and mirror groups are not a question about rules: "hang one shared stack in
       * these viewports" is a question about identity. The two come apart routinely, since the form
       * classifies by series description and a study can hold two series with one description -- so
       * two viewports holding genuinely different series store byte-identical criteria. Deriving
       * mirror groups from those criteria mirrors viewports that must not be (VX-5005), and there is
       * no criteria-based rule that would not (VX-5006 is the same collision from the other side).
       *
       * Absent means "the user has not retargeted this viewport", not "no series": the save then falls
       * back to what the viewer has hung there. Cleared, rather than left stale, whenever an edit
       * changes which series the viewport would match -- see `HangingProtocolViewport`.
       */
      seriesSmid?: Nullable<string>;
      /**
       * When this window's user last picked this viewport's `comparisonIndex` from the study
       * dropdown. Absent means nobody has declared a scope here since the form opened.
       *
       * Exists because `comparisonIndex` is the one viewport field that is **neither derived nor
       * recomputed**. Everything else the merge fn arbitrates can be re-established from something:
       * criteria come back from the classifier, source text comes back from the step, the resolved
       * series comes back from the next match. A declared scope has no such source -- it is a
       * decision only the radiologist holds -- so a broadcast carrying an older copy of it does not
       * merely delay the value, it destroys it. Silently, too: the form then genuinely equals its
       * baseline, so Update closes without sending a mutation and there is no error to notice.
       *
       * A stamp is the fix because the missing input was *ordering*, not ownership. The two
       * ownership-shaped rules both fail, and symmetrically:
       *
       *  - "keep local when the series matches" rejects the *owning* window's genuine declaration
       *    too, and the save is generated from whichever window drives the form, so that persists a
       *    stale scope for every viewport the driving window does not own.
       *  - "non-null wins" cannot express un-declaring a scope back to Current (`null`), so the
       *    owning window reverts its own edit off the other window's echo -- the same bug with new
       *    coordinates.
       *
       * Comparing stamps decides both correctly, because an echo is by definition a re-transmission
       * of a value that was *already* stamped: it carries the older stamp and loses, whichever way
       * the nullness falls. `Date.now()` rather than a counter, because two windows incrementing
       * independently cannot be ordered against each other, while both read the same OS clock.
       *
       * Session-only, like `seriesSmid`: this is cross-window bookkeeping, not a matching rule, and
       * `stepToGqlInput` never sees it.
       */
      scopeDeclaredAt?: number;
    };
  };
};

// Fallback criteria source text for a PDF viewport with no series description; the classifier
// maps this word to the DOCUMENT_TYPE category.
export const PDF_FALLBACK_SERIES_DESCRIPTION = 'document';

/**
 * The value a window broadcasts when its form closes. Distinct from `defaultHPFormState` only by
 * `closeSignal`, which is what lets the other windows tell a real close from an idle window's
 * default state.
 */
export const hpFormCloseState: HangingProtocolFormState = {
  hangingProtocolName: '',
  studyCriteriaSourceText: '',
  studyClassification: [],
  ontologyVersion: '',
  submitFormIntent: SUBMIT_FORM_INTENT.SAVE,
  hpSmid: null,
  closeSignal: true,
  viewports: {},
};

/**
 * What the form writes when a session ends in a successful submit: a close for the other windows, and
 * this window's own state kept as it was.
 *
 * The close is for the peers. Each one resets to the default on `closeSignal` and ignores the rest of
 * the payload, which is how viewer/1 drops the saved session's window-1 viewports (VX-5148).
 *
 * This window keeps its own viewports because they are what lets an Update opened straight after the
 * save classify before the reader clicks: its panels mount holding the same descriptions and start
 * classifying at once, instead of waiting for the form's init. Clearing them meant a Save clicked before
 * those classifications landed was refused by the classification gate (VX-4977) and the form stayed
 * open.
 *
 * Other windows' viewports are dropped here, not kept. The next open's eager write spreads this state
 * and broadcasts it, so holding a peer's entries would hand them straight back to the window the close
 * just cleared.
 */
export function formStateAfterSubmit(
  prev: HangingProtocolFormState,
  localWindowId: string
): HangingProtocolFormState {
  const viewports = Object.fromEntries(
    Object.entries(prev.viewports).filter(
      ([viewportId]) => viewportIdToConfig(viewportId).windowId === localWindowId
    )
  );
  return { ...prev, viewports, closeSignal: true };
}

export const defaultHPFormState: HangingProtocolFormState = {
  hangingProtocolName: '',
  studyCriteriaSourceText: '',
  studyClassification: [],
  ontologyVersion: '',
  submitFormIntent: SUBMIT_FORM_INTENT.SAVE,
  hpSmid: null,
  viewports: {},
};

/**
 * Eagerly applies a mode + HP identity change to an existing form state, for callers that write
 * `submitFormIntent`/`hpSmid` ahead of the form's own full re-init (e.g. HangingProtocolWidget's
 * `open()` and its URL-param effect, and HangingProtocolsFormV4's own eager-set effect).
 *
 * Both fields are set together intentionally: the merge fn's HP-switch discriminator
 * (`hangingProtocolFormStateMergeFn`) compares `hpSmid` to tell a same-HP broadcast race from a
 * real HP switch, so `submitFormIntent` flipping to UPDATE must always carry the matching `hpSmid`
 * in the same write -- never a stale one left over from `prev`.
 *
 * `closeSignal` is always cleared to `false`. `hpFormCloseState` (broadcast on form close) sets it
 * `true` and nothing else ever resets it, since it is meant to be a one-shot flag consumed by the
 * merge fn. Any eager write that spreads `prev` without clearing it would carry a stale `true`
 * forward, and the merge fn treats `closeSignal: true` as an authoritative close -- silently
 * resetting every window back to the default "creating" state regardless of the mode just set
 * here (VX-3589).
 */
export const withFormIntent = (
  prev: HangingProtocolFormState,
  mode: SubmitFormIntent,
  hpSmid: Nullable<string>
): HangingProtocolFormState => ({
  ...prev,
  submitFormIntent: mode,
  hpSmid: mode === SUBMIT_FORM_INTENT.UPDATE ? (hpSmid ?? null) : null,
  closeSignal: false,
});

/**
 * True when a value is indistinguishable from `defaultHPFormState`, i.e. it carries no form content
 * at all. Checked field by field rather than by deep-equality against the constant so a field added
 * to the type later cannot silently widen what counts as "empty".
 */
const isDefaultFormBroadcast = (value: HangingProtocolFormState): boolean =>
  value.submitFormIntent === SUBMIT_FORM_INTENT.SAVE &&
  Object.keys(value.viewports).length === 0 &&
  (value.hpSmid ?? null) == null &&
  value.hangingProtocolName === '' &&
  value.studyCriteriaSourceText === '' &&
  value.studyClassification.length === 0;

/** True when this window is showing a form the user could be editing. */
const isFormOpenLocally = (value: HangingProtocolFormState): boolean =>
  value.submitFormIntent === SUBMIT_FORM_INTENT.UPDATE ||
  Object.keys(value.viewports).length > 0 ||
  (value.hpSmid ?? null) != null;

/**
 * Merge function for cross-window state synchronization.
 * This handles a race condition where a secondary window (e.g., viewer/1) may broadcast
 * its initial default state before receiving the synchronized state from the primary window.
 *
 * The merge strategy:
 * - For study-level fields: prefer non-empty values (the window with the form has the real data)
 * - For viewports: merge all viewports from both windows (each window manages its own viewports)
 */
export const hangingProtocolFormStateMergeFn = (
  oldValue: Nullable<HangingProtocolFormState>,
  newValue: HangingProtocolFormState
): HangingProtocolFormState => {
  // A real form close, marked as such by the window that closed it. Accepted wholesale so stale
  // viewport entries are cleared everywhere and no window is left locked in UPDATE. `closeSignal`
  // is dropped on the way in so the flag does not linger as state.
  //
  // Checked before the no-local-value case below, which would otherwise adopt the close verbatim. A
  // window that has never written this atom would then hold `closeSignal: true` as state, and its
  // next write that spreads `prev` would broadcast it again as a close nobody made.
  if (newValue.closeSignal === true) {
    return { ...defaultHPFormState };
  }

  if (!oldValue) return newValue;

  // An unmarked default is not a close -- it is a window that simply has no form open, e.g. a second
  // viewer that just connected. It must not wipe a form the user is editing here. Shape alone cannot
  // separate the two cases, which is why the close is marked; before it was, any such broadcast blanked
  // the open form. The reported symptom was the name field coming up empty and submit then failing
  // validation on an HP that plainly has a name, cured by closing and reopening because that re-runs
  // the form's own initialization.
  if (isDefaultFormBroadcast(newValue) && isFormOpenLocally(oldValue)) {
    return oldValue;
  }

  // HP-switch discriminator: when the form moves to a different HP (or between
  // create and editing a specific HP), the old state is stale and must not bleed
  // across via the cross-window-preserve rule below. Take the incoming broadcast
  // wholesale. Legacy writers that never set hpSmid leave it null on both sides,
  // so this is a no-op for them (behavior unchanged).
  if ((oldValue.hpSmid ?? null) !== (newValue.hpSmid ?? null)) {
    return newValue;
  }

  // Same shape as above but with no form open locally: nothing to protect, so take it. Keeps a
  // window that never had a form from accumulating another window's stale viewport entries.
  if (isDefaultFormBroadcast(newValue)) {
    return newValue;
  }

  // Viewport IDs follow the pattern Viewport-{windowId}-{row}-{col}.
  // Extract which window IDs the incoming broadcast covers so we can:
  //  1. Accept all viewports from the broadcast (merging criteria when needed)
  //  2. Preserve old viewports from DIFFERENT windows (cross-window state)
  //  3. Drop stale old viewports from the SAME windows (prevents accumulation)
  //
  // Layout shrink is handled here automatically: when a window broadcasts
  // with fewer viewports, newWindowIds still contains that window's ID, so
  // old viewport entries from that window that were not re-broadcast are
  // dropped in the second loop below.
  const newWindowIds = new Set<string>();
  for (const id of Object.keys(newValue.viewports)) {
    newWindowIds.add(viewportIdToConfig(id).windowId);
  }

  const mergedViewports: HangingProtocolFormState['viewports'] = {};
  const studyDescription = newValue.studyCriteriaSourceText || oldValue.studyCriteriaSourceText;

  // Include all viewports from the incoming broadcast, preserving local criteria
  // when the broadcast clears them (stale echo or async classifier race).
  for (const [id, newVp] of Object.entries(newValue.viewports)) {
    const oldVp = oldValue.viewports[id];
    const oldHasCriteria = (oldVp?.seriesClassificationCriteria?.length ?? 0) > 0;
    const newHasCriteria = (newVp.seriesClassificationCriteria?.length ?? 0) > 0;
    // A broadcast naming a *different* series is a real change, not an echo. Missing criteria alone
    // cannot tell the two apart: `stepViewportToFormViewport` omits the key entirely for a step
    // viewport with no criteria, and the classifier populates it asynchronously, so a genuine step
    // switch routinely broadcasts the new step's source text with no criteria yet attached. Treating
    // that as an echo kept the previous step's source text (the old `||` preferred it whenever it was
    // non-empty) and pinned the other window's panel to the step the user had just left -- permanently,
    // since nothing re-broadcasts it. An empty incoming source text is still an echo and still loses.
    const newSourceText = newVp.seriesCriteriaSourceText ?? '';
    const oldSourceText = oldVp?.seriesCriteriaSourceText ?? '';
    const namesADifferentSeries = newSourceText !== '' && newSourceText !== oldSourceText;
    const expectedSignature = viewportClassifierSignature(newVp, studyDescription ?? '');
    const incomingCriteriaMatch = newVp.criteriaSignature === expectedSignature;
    const localCriteriaMatch = oldVp?.criteriaSignature === expectedSignature;

    // The declared prior scope, arbitrated by stamp rather than by branch. See `scopeDeclaredAt` on
    // `HangingProtocolFormState` for why ordering is the right question and ownership is not.
    //
    // Scoped to the same-series case deliberately: a broadcast that names a *different* series is a
    // genuine retarget, and the scope declared alongside it has to cross even when this window holds
    // a newer stamp for the series being replaced -- that stamp describes a viewport that no longer
    // exists. Within one series, the newer stamp wins in both directions, which is what makes
    // un-declaring back to Current survive an echo.
    const oldStamp = oldVp?.scopeDeclaredAt ?? 0;
    const newStamp = newVp.scopeDeclaredAt ?? 0;

    /**
     * The newer stamp wins. `tieBreaksLocal` decides only the case where neither side is newer,
     * and it differs per branch because the branches hold different evidence.
     *
     * The criteria-lost branch below has independent evidence of an echo -- a broadcast that
     * dropped its classification is far more likely re-transmitting a stale copy than describing a
     * fresh edit -- so an unstamped tie there keeps local, which is what it did before stamps
     * existed. The wholesale branch has no such evidence, so an unstamped tie takes the broadcast,
     * which is also what it did before. Stamps therefore only ever *add* an answer; on a viewport
     * nobody has declared a scope for, neither branch changes behaviour.
     */
    const resolveDeclaredScope = (tieBreaksLocal: boolean): number | null => {
      if (oldVp == null || namesADifferentSeries) return newVp.comparisonIndex;
      if (newStamp > oldStamp) return newVp.comparisonIndex;
      if (oldStamp > newStamp) return oldVp.comparisonIndex;
      return tieBreaksLocal ? oldVp.comparisonIndex : newVp.comparisonIndex;
    };
    // Carry the winning stamp forward, so the value keeps its ordering when this window rebroadcasts.
    // A merged value that dropped its stamp would present a fresh declaration as an undeclared one
    // and lose the next exchange to the very echo it just beat. On a retarget the incoming entry is
    // authoritative for the new series, so its stamp travels with it rather than the local maximum.
    const declaredScopeStamp = namesADifferentSeries
      ? newVp.scopeDeclaredAt
      : Math.max(oldStamp, newStamp) || undefined;

    if (
      oldVp != null &&
      !namesADifferentSeries &&
      !incomingCriteriaMatch &&
      (localCriteriaMatch || (oldHasCriteria && !newHasCriteria))
    ) {
      // A peer can echo populated criteria for the previous study description after the local
      // classifier has already answered the new one. Keep the answer matching the current inputs;
      // the classifier will not run again just because an echo replaced its signature.
      // Also protect criteria from initialization echoes that omit them. A signed empty result
      // is a real answer and bypasses this branch, so it can replace an older classification.
      // Preserve both classification criteria AND source text from local state
      // so step-loaded values like "R CC" aren't overwritten by empty strings.
      mergedViewports[id] = {
        ...newVp,
        seriesClassificationCriteria: oldVp.seriesClassificationCriteria,
        // The signature comes with the criteria it describes. Taking one from the local copy and the
        // other from the broadcast leaves a viewport holding real criteria that claim to describe
        // inputs they did not, which reads as still-waiting and holds the save with nothing the
        // reader can do: the query behind it already succeeded, so there is no error for Try again
        // to retry.
        criteriaSignature: oldVp.criteriaSignature,
        failedSignature: oldVp.failedSignature,
        seriesCriteriaSourceText: oldSourceText || newSourceText,
        // Resolved by stamp like every other path, rather than pinned to local. This branch is a
        // broadcast that *lost* its criteria, which is strong evidence of an echo but not proof: the
        // classifier populates criteria asynchronously, so the owning window's genuine declaration
        // can arrive here too, before its own classifier has run. Pinning local reverted exactly
        // those. The stamp separates them without having to guess from the criteria.
        comparisonIndex: resolveDeclaredScope(true),
        // Spread conditionally rather than assigned: `scopeDeclaredAt: undefined` puts the *key* on
        // the object, and this value is deep-compared in several places (ramda's `equals` counts a
        // key holding `undefined` as a difference). A viewport nobody has declared a scope for must
        // come out of this merge byte-identical to the way it went in.
        ...(declaredScopeStamp != null ? { scopeDeclaredAt: declaredScopeStamp } : {}),
      };
    } else {
      // Wholesale, criteria included: when the series changes, the old classification described the
      // series being replaced, so carrying it over would show the previous step's criteria under the
      // new series' name.
      //
      // `comparisonIndex` is the exception, and this is the path the live failure took. A window
      // echoing a viewport it has already classified keeps its criteria, so the echo branch above
      // does not catch it and the incoming entry was taken whole -- stale declared scope included --
      // on a broadcast that is an echo by every other measure. Same series on both sides here, so
      // the stamp is what decides.
      mergedViewports[id] = { ...newVp, comparisonIndex: resolveDeclaredScope(false) };
      if (declaredScopeStamp != null) {
        mergedViewports[id].scopeDeclaredAt = declaredScopeStamp;
      }
    }
  }

  // Preserve old viewports from windows NOT represented in the broadcast.
  // This keeps cross-window state intact during the race where one window
  // broadcasts before receiving the other window's viewports.
  for (const [id, oldVp] of Object.entries(oldValue.viewports)) {
    if (id in mergedViewports) continue;
    const { windowId } = viewportIdToConfig(id);
    if (!newWindowIds.has(windowId)) {
      mergedViewports[id] = oldVp;
    }
  }

  return {
    // Prefer non-empty study-level fields
    hangingProtocolName: newValue.hangingProtocolName || oldValue.hangingProtocolName,
    studyCriteriaSourceText: studyDescription,
    studyClassification:
      newValue.studyClassification.length > 0
        ? newValue.studyClassification
        : oldValue.studyClassification,
    ontologyVersion: newValue.ontologyVersion || oldValue.ontologyVersion,
    // Protect UPDATE intent from stale broadcast echoes: when the form is open
    // (UPDATE + viewports populated), a delayed echo with SAVE must not overwrite it.
    // But when the form closes (broadcasts defaultHPFormState with SAVE + empty viewports),
    // all windows must accept the SAVE so UPDATE doesn't get stuck permanently.
    submitFormIntent:
      oldValue.submitFormIntent === SUBMIT_FORM_INTENT.UPDATE &&
      Object.keys(newValue.viewports).length > 0
        ? SUBMIT_FORM_INTENT.UPDATE
        : newValue.submitFormIntent,
    // Same HP on this path (the switch case returned early), so either side's
    // hpSmid is correct; carry it through.
    hpSmid: newValue.hpSmid,
    // A delayed peer echo cannot undo a newer initialization or cause repeated cache replays.
    ...(oldValue.initializationVersion != null || newValue.initializationVersion != null
      ? {
          initializationVersion: Math.max(
            oldValue.initializationVersion ?? 0,
            newValue.initializationVersion ?? 0
          ),
        }
      : {}),
    viewports: mergedViewports,
  };
};

/**
 * Builds the form's viewport entries from a step: the viewports the step defines, plus a cleared
 * placeholder for every layout slot it does not.
 *
 * `restrictToWindowId` decides which windows this call is allowed to speak for.
 *
 * In UPDATE the stored protocol is authoritative for every window, so the caller leaves it
 * undefined -- including for windows the user does not currently have open, whose configuration
 * must survive the round trip rather than be dropped from the next save.
 *
 * In CREATE it is not authoritative for anything. There is no stored protocol yet; each window
 * seeds its own viewports from what it currently has hung, in `HangingProtocolViewport`'s init
 * effect. `hangingProtocolFormState` is BroadcastChannel-synced, and `hangingProtocolFormStateMergeFn`
 * treats a broadcast that covers a window as authoritative for that window. So emitting cleared
 * placeholders for the other window overwrites the series the radiologist hung there -- and nothing
 * puts them back, because that init effect runs once per mount and refuses to write for a window
 * other than its own. Worse, the ordering is reliably against us: the sidebar-state broadcast that
 * opens the panels on viewer/1 arrives before this state does, so viewer/1 has always written its
 * viewports by the time the placeholders land on top of them.
 *
 * Symptom when this is unscoped: hang series on viewer/1, click Create HP on viewer/0, and every
 * viewer/1 panel reads "Clear Viewport".
 */
export function buildStepFormViewports({
  step,
  layouts,
  restrictToWindowId,
  studyDescription = '',
}: {
  step: StepConfiguration;
  layouts: Layouts;
  restrictToWindowId?: string;
  /** The protocol's study description, so seeded criteria can say what they describe. */
  studyDescription?: string;
}): HangingProtocolFormState['viewports'] {
  const owns = (windowId: string): boolean =>
    restrictToWindowId == null || windowId === restrictToWindowId;

  const viewports: HangingProtocolFormState['viewports'] = {};

  for (const [viewportId, entry] of Object.entries(step.viewports)) {
    if (!owns(viewportIdToConfig(viewportId).windowId)) continue;
    viewports[viewportId] = stepViewportToFormViewport(entry, studyDescription);
  }

  // Fill missing layout positions as cleared. Check both the step's layouts AND the current viewer
  // layouts so that windows not defined in the step (e.g. window 1 in dual-monitor mode) still get
  // their viewports marked as cleared.
  const allLayouts: Record<string, [number, number]> = {};
  for (const [windowId, layoutStr] of Object.entries(step.layouts)) {
    allLayouts[windowId] = parseLayoutString(layoutStr);
  }
  for (const [windowId, layout] of Object.entries(layouts)) {
    allLayouts[windowId] ??= layout;
  }

  for (const [windowId, [rows, cols]] of Object.entries(allLayouts)) {
    if (!owns(windowId)) continue;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const vpId = generateViewportId(windowId, row, col);
        viewports[vpId] ??= {
          seriesCriteriaSourceText: '',
          comparisonIndex: null,
          isClearViewport: true,
          viewType: ViewType.TwoDDre,
        };
      }
    }
  }

  return viewports;
}

/**
 * Flattens a classification object (keyed by snake_case category) into an array of
 * { category, value, displayName } using the provided category enum map.
 */
function flattenClassification<C extends string>(
  classification: unknown,
  categoryMap: Record<string, C>
): Array<{ category: C; value: string; displayName: string }> {
  const result: Array<{ category: C; value: string; displayName: string }> = [];
  if (classification == null || typeof classification !== 'object') return result;

  for (const [key, values] of Object.entries(classification)) {
    const category = categoryMap[key];
    if (category && Array.isArray(values)) {
      values.forEach((item) => {
        if (item?.ontology_id) {
          result.push({
            category,
            value: item.ontology_id,
            displayName: item.display_name,
          });
        }
      });
    }
  }
  return result;
}

const STUDY_CATEGORY_MAP: Record<string, StudyCriteriaCategory> = {
  anatomic_focus: StudyCriteriaCategory.AnatomicFocus,
  contrast_status: StudyCriteriaCategory.ContrastStatus,
  field_of_view: StudyCriteriaCategory.FieldOfView,
  modality: StudyCriteriaCategory.Modality,
  reason_for_study: StudyCriteriaCategory.ReasonForStudy,
  view_count: StudyCriteriaCategory.ViewCount,
};

const SERIES_CATEGORY_MAP: Record<string, SeriesCriteriaCategory> = {
  anatomic_focus: SeriesCriteriaCategory.AnatomicFocus,
  contrast_status: SeriesCriteriaCategory.ContrastStatus,
  ct_kernel: SeriesCriteriaCategory.CtKernel,
  dimensionality: SeriesCriteriaCategory.Dimensionality,
  document_type: SeriesCriteriaCategory.DocumentType,
  field_of_view: SeriesCriteriaCategory.FieldOfView,
  localizer: SeriesCriteriaCategory.Localizer,
  modality: SeriesCriteriaCategory.Modality,
  mr_relative_phase: SeriesCriteriaCategory.MrRelativePhase,
  mr_weighting: SeriesCriteriaCategory.MrWeighting,
  patient_position: SeriesCriteriaCategory.PatientPosition,
  post_processing: SeriesCriteriaCategory.PostProcessing,
  tissue_selection: SeriesCriteriaCategory.TissueSelection,
  view_orientation: SeriesCriteriaCategory.ViewOrientation,
};

/**
 * Merge a live step's viewports (from `steppedHPConfigurations`) with the same step's
 * raw-fetched viewports (from a direct backend fetch) for UPDATE-mode form init.
 *
 * The raw fetch has complete, unfiltered viewport definitions — including criteria-based
 * viewports the match query stripped because the current case has no matching series. The live
 * atom may instead hold a clinical edit (e.g. a drag-and-drop series assignment) that hasn't
 * been persisted to the backend yet, which the raw fetch can't know about.
 *
 * Per viewport, prefer the live entry when it's non-empty (`isEmptyViewport` — has a display
 * name, criteria, or series criteria text), since that only happens via a resolved criteria match
 * or a clinical edit, both more current than the backend copy. Otherwise fall back to the fetched
 * entry so an unmatched criteria-based viewport definition stays visible/editable instead of
 * appearing as a cleared, empty viewport (VX-4614).
 */
export function mergeStepViewportsForUpdate(
  liveViewports: Record<string, ViewportConfigurationEntry>,
  fetchedViewports: Record<string, ViewportConfigurationEntry>
): Record<string, ViewportConfigurationEntry> {
  const viewportIds = new Set([...Object.keys(liveViewports), ...Object.keys(fetchedViewports)]);
  const merged: Record<string, ViewportConfigurationEntry> = {};
  for (const id of viewportIds) {
    const live = liveViewports[id];
    const fetched = fetchedViewports[id];
    merged[id] = live != null && !isEmptyViewport(live) ? live : (fetched ?? live)!;
  }
  return merged;
}

export function flattenStudyClassification(classification: unknown): Array<ExtendedStudyCriteria> {
  return flattenClassification(classification, STUDY_CATEGORY_MAP);
}

export function flattenSeriesClassification(
  classification: unknown
): Array<ExtendedSeriesCriteria> {
  return flattenClassification(classification, SERIES_CATEGORY_MAP);
}

/**
 * True when the steps the form would send are identical to the ones it started from, i.e. this save
 * changes nothing about the HP's steps.
 *
 * Compares the GQL inputs rather than the `StepConfiguration` objects, so the answer is about what
 * would actually go over the wire: two step lists that serialize identically produce the same
 * backend write regardless of how their in-memory shapes differ. That also makes the check safe by
 * construction -- a difference the comparison invents (the form's step sync rewrites viewport
 * formats and accumulates layout changes) only costs the caller the chance to omit `steps`, while
 * missing a real difference would drop the user's edit. It errs toward "changed", never toward
 * silently discarding step content. Note this is deliberately stricter than `hasUnsavedChanges`,
 * which compares only a step's name, smid and config_details.
 */
export const areStepsUnchanged = (
  currentSteps: StepConfiguration[] | null,
  initialSteps: StepConfiguration[] | null,
  stepViewportSeriesIds?: Nullable<Record<string, ViewportSeriesIds>>
): boolean => {
  if (currentSteps == null || initialSteps == null) return false;
  if (currentSteps.length !== initialSteps.length) return false;
  // Asymmetric on purpose. The current side is projected through the same series map the payload
  // would use, so a re-grouping shows up as a difference; the initial side is projected without one,
  // so its stored mirror groups stand as "what it was". Passing the map to both sides would make a
  // mirror-only change invisible and `preserveExistingSteps` would then omit `steps` entirely --
  // dropping the very edit that prompted the save. That is reachable: two viewports can swap between
  // holding one series and two while their criteria stay byte-identical (VX-5005/VX-5006).
  return equals(
    currentSteps.map((step) =>
      toComparableStepInput(step, stepViewportSeriesIds?.[step.smid ?? ''])
    ),
    initialSteps.map((step) => toComparableStepInput(step))
  );
};

/**
 * Normalizes a step's GQL input into a form whose deep equality reflects content alone.
 *
 * `stepToGqlInput` builds its `layouts` and `viewports` arrays with `Object.entries`, so their order
 * follows key insertion order rather than anything meaningful. Form step sync and BroadcastChannel
 * round trips both produce different insertion orders for identical data, which a plain deep compare
 * reads as a change -- the same hazard `hasUnsavedChanges` defends against by sorting its viewport
 * entries. Left unsorted it would silently resend `steps` on a name-only save and reintroduce the
 * smid churn this is meant to avoid. Sorting happens here rather than in `stepToGqlInput` so the
 * payload actually sent to the backend is untouched.
 *
 * `sub_steps` keeps its array order, which is the sub-step sequence and therefore meaningful; only
 * the viewports within each are sorted.
 */
const toComparableStepInput = (
  step: StepConfiguration,
  seriesIds?: Nullable<ViewportSeriesIds>
) => {
  const input = stepToGqlInput(step, seriesIds);
  const sortByViewportId = <T extends { viewport_id: string }>(viewports: readonly T[]) =>
    [...viewports].sort((a, b) => a.viewport_id.localeCompare(b.viewport_id));
  const sortByWindowId = <T extends { window_id: string }>(layouts: readonly T[]) =>
    [...layouts].sort((a, b) => a.window_id.localeCompare(b.window_id));

  return {
    ...input,
    layouts: sortByWindowId(input.layouts ?? []),
    viewports: sortByViewportId(input.viewports ?? []),
    sub_steps:
      input.sub_steps?.map((subStep) => ({
        ...subStep,
        viewports: sortByViewportId(subStep.viewports ?? []),
      })) ?? input.sub_steps,
  };
};

export const generatePayload = ({
  formState,
  layouts,
  numberOfPriors,
  steps,
  studyDescriptions,
  groupedStudies,
  preserveExistingSteps = false,
  viewportSeriesIds,
  stepViewportSeriesIds,
  storedMirrorGroupIds,
}: {
  formState: HangingProtocolFormState;
  layouts: Layouts;
  numberOfPriors: number;
  steps?: StepConfiguration[] | null;
  studyDescriptions?: string[];
  groupedStudies?: string[];
  /**
   * Send `preserveExistingSteps` instead of the `steps` array, leaving the backend's existing step
   * rows untouched. Only valid on an update whose step content is unchanged (VX-4766).
   */
  preserveExistingSteps?: boolean;
  /**
   * What the viewer currently holds in each viewport, for the flat `data.viewportsConfigurations`
   * branch. Omit it and no viewport is mirrored -- see the mirror-group comment below.
   */
  viewportSeriesIds?: Nullable<ViewportSeriesIds>;
  /**
   * The same, per step smid, for the `steps` branch. A step missing from the record keeps whatever
   * mirror groups it already carries rather than having them recomputed from nothing.
   */
  stepViewportSeriesIds?: Nullable<Record<string, ViewportSeriesIds>>;
  /**
   * The mirror groups the protocol being updated already stores, keyed by viewport id, from the form's
   * case-less raw fetch. Omitted on create, where there is nothing stored to carry.
   */
  storedMirrorGroupIds?: Nullable<Record<string, number | null>>;
}) => {
  // When steps are present, the backend reads per-step layouts from step.layouts,
  // not from data.layouts. The frontend still sends data.layouts for non-stepped HPs
  // and as a fallback for the HP's "base" layout (used in the HP list/preview).
  // Each step carries its own layouts (step.layouts). The backend ignores data.layouts when steps are
  // present, so this value only matters for non-stepped HPs.
  const layoutsPayload = Object.entries(layouts).map(([id, layout]) => ({
    id,
    layout,
  }));

  // Filter out viewportIds that are not visible in the layouts or are marked as cleared
  const visibleViewports = Object.entries(formState.viewports).filter(
    ([viewportId, viewportConfig]) => {
      // Filter out cleared viewports
      if (viewportConfig.isClearViewport) return false;

      try {
        const { windowId, rowIndex, colIndex } = viewportIdToConfig(viewportId);
        const layout = layoutsPayload.find((layout) => layout.id === windowId)?.layout;
        if (!layout) return false;
        return layout[0] > rowIndex && layout[1] > colIndex;
      } catch (err) {
        logger.warn('[generatePayload] Skipping viewport with invalid ID', { viewportId, err });
        return false;
      }
    }
  );

  // Mirror groups: which viewports the backend should hang one shared stack in. Keyed on the series
  // each viewport actually holds wherever there is one, because a protocol is stored criteria-only and
  // two viewports holding different series routinely store identical criteria (VX-5005). This branch
  // previously emitted no `viewportMirrorGroupId` at all, so one series hung deliberately in two
  // viewports came back as two different series (VX-5006). A viewport that resolved to nothing falls
  // back to its criteria, which is what lets a form authored with nothing hung still record the
  // mirroring the author asked for; with neither it stays ungrouped.
  //
  // When nothing resolved, the criteria fallback is allowed to *inform* a group but never to
  // overwrite one: a protocol that already stores mirror groups keeps them, exactly as the stepped
  // path does (`applyMirrorGroupsToStepViewports`). Without that, an edit made from a case where no
  // viewport resolves -- a rename, or criteria that match nothing here -- rewrote the stored groups
  // from criteria alone, which is the inference the tech spec rejected: it can merge two viewports
  // the author meant to hold different series, and it can split a group the author did ask for.
  // Create still takes the criteria tier, since there is nothing stored to protect.
  const storedMirrorGroups = visibleViewports.map(
    ([viewportId]) => storedMirrorGroupIds?.[viewportId] ?? null
  );
  // Scoped to the viewports being sent, not the whole map. The map is keyed by viewport id across
  // grids and windows, so it routinely carries entries for slots this layout does not show -- and one
  // resolved leftover made "nothing resolved" read false while every viewport in the payload had no
  // series. The criteria tier then ran on exactly the evidence-free viewports this guard exists to
  // protect. Found by the live update spec, which saw all three viewports come back in one group.
  const visibleSeriesIds = Object.fromEntries(
    visibleViewports.map(([viewportId]) => [viewportId, viewportSeriesIds?.[viewportId]])
  );
  const mustPreserveStoredMirrorGroups =
    hasNoResolvedSeries(visibleSeriesIds) && storedMirrorGroups.some((id) => id != null);

  const mirrorGroupIds = mustPreserveStoredMirrorGroups
    ? storedMirrorGroups
    : assignMirrorGroupIds(
        visibleViewports.map(([viewportId, viewportConfig]) => ({
          id: viewportId,
          comparisonIndex: viewportConfig.comparisonIndex,
          criteria: viewportConfig.seriesClassificationCriteria,
        })),
        viewportSeriesIds ?? {}
      );

  const viewportsConfigurationsPayload = visibleViewports.map(
    ([viewportId, viewportConfig], index) => ({
      id: viewportId,
      comparisonIndex: viewportConfig.comparisonIndex,
      seriesCriteriaSourceText: viewportConfig.seriesCriteriaSourceText,
      seriesDescriptions: [],
      viewType: viewportConfig.viewType,
      viewportMirrorGroupId: mirrorGroupIds[index],
      criteria: (viewportConfig.seriesClassificationCriteria ?? []).map((criteria) => ({
        category: criteria.category,
        value: criteria.value,
      })),
    })
  );

  const payload = {
    name: formState.hangingProtocolName,
    // Send undefined instead of empty string - backend rejects blank but accepts omitted
    ontologyVersion: formState.ontologyVersion || undefined,
    studyCriteriaSourceText: formState.studyCriteriaSourceText,
    studyDescriptions: studyDescriptions ?? [],
    numberOfPriors: numberOfPriors,
    groupedStudies: groupedStudies ?? [],
    studyCriteria: formState.studyClassification.map((criteria) => ({
      category: criteria.category,
      value: criteria.value,
    })),
    data: {
      layouts: layoutsPayload,
      viewportsConfigurations: viewportsConfigurationsPayload,
    },
    ...(steps != null && steps.length > 0
      ? preserveExistingSteps
        ? // Omitting `steps` is what keeps the backend from deleting and recreating every step row;
          // the flag tells the resolver this is a stepped HP so it doesn't fall back to sending
          // layouts/viewports, which the backend rejects for an HP that has steps.
          { preserveExistingSteps: true }
        : {
            steps: steps.map((step) =>
              stepToGqlInput(step, stepViewportSeriesIds?.[step.smid ?? ''])
            ),
          }
      : {}),
  };

  return payload as HangingProtocolPayload;
};

/**
 * Which of the two situations a dirty check is being asked about, with respect to step layouts.
 *
 * A caller must state one. There is no default and no "unset", because the whole point of this enum
 * is that skipping the step-layout comparison should be a claim the caller makes, not a field it
 * omitted. See {@link hasUnsavedChanges} for the defect history behind that.
 */
export enum StepLayoutContextKind {
  /**
   * There is no HP-owned layout to compare against at all -- e.g. CREATE, where there is no
   * existing HP whose stored layout could differ from the current viewer layout.
   *
   * `hasUnsavedChanges` then answers purely from the form-state and step-configuration
   * comparisons. Choosing this for an UPDATE (stepped or flat) reintroduces VX-4771/VX-4938: the
   * layout picker writes to the viewer's layout atoms, invisible to every other comparison in
   * `hasUnsavedChanges`, so a layout-only edit reads as clean and Update becomes a silent no-op.
   */
  None = 'none',

  /**
   * A step is active, so its stored layout has to be compared against the layout a save would write.
   *
   * Requires `activeStepLayouts` (what the step has now) and `currentLayouts` (what a save would put
   * there). A difference means unsaved changes, and nothing else in `hasUnsavedChanges` can see it:
   * the layout picker writes to the viewer's layout atoms, and the layout only becomes part of the
   * step inside `flushCurrentStepToConfigurations()`, which runs after the check.
   *
   * Read both from the same source that flush reads, or the check and the save can disagree about
   * what is about to be written.
   */
  ActiveStep = 'activeStep',

  /**
   * Editing a flat (non-stepped) HP, so the HP's own stored `data.layouts` has to be compared
   * against the layout a save would write.
   *
   * Same blind spot as `ActiveStep`, just for the non-stepped shape: the layout picker writes to
   * the viewer's layout atoms, which `generatePayload` always sends as `data.layouts` regardless
   * of whether anything else on the form changed. Without this, a layout-only edit on a flat HP is
   * invisible to `hasUnsavedChanges` and Update silently no-ops (VX-4938).
   */
  FlatHp = 'flatHp',
}

/**
 * Whether an HP-owned layout is in play for a dirty check, and the data to compare if it is.
 *
 * See {@link hasUnsavedChanges} for why this is a union a caller must fill in rather than a pair of
 * optional fields.
 */
export type StepLayoutContext =
  | { kind: StepLayoutContextKind.None }
  | {
      kind: StepLayoutContextKind.ActiveStep;
      /** The active step's stored layouts, `windowId -> "rows,cols"`. */
      activeStepLayouts: Record<string, string>;
      /** The layouts a save would write, from the same source `flushCurrentStepToConfigurations` reads. */
      currentLayouts: Nullable<Layouts>;
    }
  | {
      kind: StepLayoutContextKind.FlatHp;
      /** The flat HP's stored layouts, `windowId -> "rows,cols"`, from `hangingProtocol.data.layouts`. */
      storedLayouts: Record<string, string>;
      /** The layouts a save would write -- the same viewer layouts `generatePayload` reads. */
      currentLayouts: Nullable<Layouts>;
    };

/** Convenience for the common "no active step" case, so callers read as intent. */
export const NO_STEP_LAYOUT_CONTEXT: StepLayoutContext = { kind: StepLayoutContextKind.None };

/**
 * A viewport's classification reduced to what the protocol actually stores, so two projections of the
 * same classification compare equal.
 *
 * Two differences have to be normalized away, and both are present on every form open rather than
 * being edge cases. `displayName` is resolved by the classifier and never persisted, so the stored
 * side carries `''` (see `stepViewportToFormViewport`) while the classified side carries a real name.
 * And ordering is not meaningful: the stored array's order comes from the backend's category order,
 * the classified one's from `flattenClassification` walking the response object's keys.
 *
 * `{ category, value }` is exactly the pair `generatePayload` sends, which is what makes this the
 * right granularity: it reports a difference precisely when a save would write something different.
 *
 * The pair is joined on `\u0000` (NUL) because a delimiter has to be a character neither an ontology id
 * nor a category can contain, or two different pairs collapse to one string and a real edit reads as
 * no change.
 *
 * Written as the escape, never as the literal byte. A literal NUL makes the whole file read as binary,
 * so `grep`/`rg` report "binary file matches" instead of the line -- which is also why it survives
 * review so easily -- and some editors mangle it on save. It is invisible either way, so the next
 * reader takes it for a stray space and "tidies" it into one.
 */
const normalizeClassificationCriteria = (
  criteria: ReadonlyArray<{ category: string; value: string }> | undefined
): string[] => (criteria ?? []).map(({ category, value }) => `${category}\u0000${value}`).sort();

/**
 * Deep comparison helper to detect if there are unsaved changes.
 * Compares form state and step configurations against their initial snapshots.
 */
export const hasUnsavedChanges = ({
  currentFormState,
  initialFormState,
  currentStepConfigurations,
  initialStepConfigurations,
  stepContext,
}: {
  currentFormState: HangingProtocolFormState;
  initialFormState: HangingProtocolFormState | null;
  currentStepConfigurations: StepConfiguration[] | null;
  initialStepConfigurations: StepConfiguration[] | null;
  /**
   * Whether there is an HP-owned layout (an active step's, or a flat HP's own) that could have
   * changed, and if so its stored layouts plus the layouts a save would write.
   *
   * Required, and a discriminated union rather than two optional fields, on purpose. A layout change
   * is invisible to every other comparison in here: the picker writes to the viewer's layout atoms,
   * and that only becomes part of the payload inside `flushCurrentStepToConfigurations()` /
   * `generatePayload()`, which runs *after* this check. Omitting it made an Update that changed only
   * a grid take the no-op early return -- first for stepped HPs (VX-4771), then again for flat HPs
   * (VX-4938) -- and made Cancel skip the discard prompt, so the same edit was discarded in silence on
   * both paths.
   *
   * Optional fields would let a future caller inherit that blind spot by saying nothing, which is how
   * the sibling layout defect became five tickets instead of one (VX-4624, VX-4732, VX-4768,
   * VX-4772, VX-4938): each fix reached only the call site its ticket travelled through. Here a caller
   * with no HP-owned layout -- CREATE, where there is nothing stored yet to diff against -- has to say
   * `{ kind: 'none' }`, so skipping the layout comparison is a decision on the record instead of a
   * field someone forgot.
   */
  stepContext: StepLayoutContext;
}): boolean => {
  // If no initial state was captured, assume no changes
  if (!initialFormState) return false;

  if (
    stepContext.kind === StepLayoutContextKind.ActiveStep &&
    stepLayoutWouldChangeOnSave({
      activeStepLayouts: stepContext.activeStepLayouts,
      currentLayouts: stepContext.currentLayouts,
    })
  ) {
    return true;
  }

  if (
    stepContext.kind === StepLayoutContextKind.FlatHp &&
    stepLayoutWouldChangeOnSave({
      activeStepLayouts: stepContext.storedLayouts,
      currentLayouts: stepContext.currentLayouts,
    })
  ) {
    return true;
  }

  // Compare form state fields (excluding submitFormIntent which is mode-specific,
  // and studyClassification/ontologyVersion which are auto-populated by the
  // classifier after init — not user-driven changes).
  if (currentFormState.hangingProtocolName !== initialFormState.hangingProtocolName) return true;
  if (currentFormState.studyCriteriaSourceText !== initialFormState.studyCriteriaSourceText)
    return true;

  // Compare user-editable viewport fields only. Exclude seriesClassificationCriteria
  // which is auto-populated by the classifier (with resolved displayNames) after init.
  const pickEditableFields = (vp: HangingProtocolFormState['viewports'][string]) => ({
    seriesCriteriaSourceText: vp.seriesCriteriaSourceText,
    comparisonIndex: vp.comparisonIndex,
    viewType: vp.viewType,
    isClearViewport: vp.isClearViewport,
  });
  // Sort entries by viewport ID so key insertion order doesn't cause false diffs.
  // BroadcastChannel sync and form init can produce different key orderings for
  // identical viewport data.
  const currentVps = Object.fromEntries(
    Object.entries(currentFormState.viewports)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, vp]) => [id, pickEditableFields(vp)])
  );
  const initialVps = Object.fromEntries(
    Object.entries(initialFormState.viewports)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, vp]) => [id, pickEditableFields(vp)])
  );
  if (!equals(currentVps, initialVps)) {
    return true;
  }

  // On an UPDATE, a classifier answer that disagrees with the criteria the protocol *stored* is the
  // one case where `seriesClassificationCriteria` is not the "auto-populated after init" noise the
  // exclusion above exists for. It is fresher information about a saved protocol, and it is only
  // reachable through this comparison: the field is excluded above, the step comparison below reads
  // `config_details` and never viewports, and the classifier writes nothing else. Left invisible, the
  // reader gets no dirty state, Update takes its no-op early return, and the improved classification
  // is dropped in silence -- while `areStepsUnchanged` would happily have sent it, had a mutation
  // fired at all (VX-4460).
  //
  // Scoped to UPDATE because on CREATE there is nothing stored to be stale: the snapshot is seeded
  // with `seriesClassificationCriteria: []` alongside the form state (see HangingProtocolViewport's
  // init effect, which mirrors both writes precisely so the seeding cannot read as an edit), and the
  // first classifier answer would otherwise make a freshly opened Create form prompt to discard
  // changes nobody made.
  //
  // `studyClassification` and `ontologyVersion` stay excluded. The same shape of gap exists there,
  // but the snapshot is never seeded with the HP's stored `studyCriteria` (it is captured as `[]`),
  // so including them needs a seeding change this does not make, and study-level criteria are not
  // what VX-4460 reports.
  //
  // Stepped protocols only, and that is a data-availability limit rather than a scoping preference.
  // `StepViewportEntry.criteria` is a complete `{ category, value }` list, so `stepViewportToFormViewport`
  // seeds the snapshot with exactly what the step stored and a missing entry genuinely means "the step
  // stored none". A flat protocol's snapshot has no criteria at all (see the init path in
  // `HangingProtocolsFormV4`), and it cannot get them: `seriesCriteriaKeys` selects 6 of
  // `SeriesCriteria`'s 15 categories and omits `view_orientation.ontology_id`, so seeding from it
  // would report a form as dirty the moment the classifier resolved a category the fragment does not
  // carry -- on every open, for every flat protocol. Widening that fragment is the prerequisite for
  // extending this, and is deliberately not done here.
  if (
    currentFormState.submitFormIntent === SUBMIT_FORM_INTENT.UPDATE &&
    currentStepConfigurations != null
  ) {
    for (const [viewportId, currentVp] of Object.entries(currentFormState.viewports)) {
      // `undefined` on the live side means no classifier has answered for this viewport yet, so there
      // is nothing to judge: `stepViewportToFormViewport` omits the field for a step that stored no
      // criteria, and the classifier fills it in only once the debounce expires. Reading that as a drop
      // would report the form dirty for the window between opening it and the round trip landing.
      //
      // On the snapshot side `undefined` is a real answer -- the step stored none -- which is why it is
      // normalized to `[]` rather than skipped. That is the "criteria appearing where the protocol has
      // none" case, and it is the most common shape of VX-4460: a protocol saved before the classifier
      // could resolve its descriptions.
      if (currentVp.seriesClassificationCriteria == null) continue;
      const initialVp = initialFormState.viewports[viewportId];
      if (initialVp == null) continue;
      if (
        !equals(
          normalizeClassificationCriteria(currentVp.seriesClassificationCriteria),
          normalizeClassificationCriteria(initialVp.seriesClassificationCriteria)
        )
      ) {
        return true;
      }
    }
  }

  // Compare step configurations using only user-meaningful fields.
  // The form's step sync adds cleared viewport entries, modifies viewport formats,
  // and accumulates layout changes that cause false diffs with naive comparison.
  if (currentStepConfigurations != null && initialStepConfigurations != null) {
    if (currentStepConfigurations.length !== initialStepConfigurations.length) return true;
    for (let i = 0; i < currentStepConfigurations.length; i++) {
      const curr = currentStepConfigurations[i];
      const init = initialStepConfigurations[i];
      if (curr.display_name !== init.display_name) return true;
      if (curr.smid !== init.smid) return true;
      if (!equals(curr.config_details, init.config_details)) return true;
    }
  } else if (currentStepConfigurations !== initialStepConfigurations) {
    return true;
  }

  return false;
};

/**
 * Whether saving now would change the active step's stored layouts.
 *
 * A layout change is invisible to {@link hasUnsavedChanges}: the layout picker writes to the
 * viewer's layout atoms, and the layout only becomes part of a step inside
 * `flushCurrentStepToConfigurations()`, which runs after the no-op check. So an Update that changed
 * only a step's grid took the early return, fired no mutation, and then `restoreInitialLayouts()`
 * put the old grid back -- the change was discarded with no error and no toast (VX-4771).
 *
 * This mirrors what the flush actually writes, so the two cannot disagree. The flush sets every
 * window present in the viewer's current layouts and preserves step-owned windows the viewer no
 * longer reports, so the stored record changes exactly when some current window's layout differs
 * from what the step has -- including a window the step does not define yet, which the flush adopts.
 *
 * Iterating the *current* windows rather than the step's own is the correction to the first VX-4771
 * fix, which compared only windows the step already owned. On a dual-window viewer whose step had
 * never stored window "1", a layout change there read as clean and was silently discarded -- the
 * configuration QA re-reported ("different layouts in Viewer 1 and Viewer 2").
 *
 * Trade-off: the first Update on a dual-window viewer now reports a change even if the user edited
 * nothing, because the flush would genuinely add that window to the step. That fires one extra
 * mutation and clears clinical dirty state in a case where it previously would not. Silently
 * dropping a layout the user explicitly picked is the worse failure, and the condition self-heals
 * once the step owns both windows.
 */
export function stepLayoutWouldChangeOnSave({
  activeStepLayouts,
  currentLayouts,
}: {
  activeStepLayouts: Record<string, string>;
  currentLayouts: Nullable<Layouts>;
}): boolean {
  if (currentLayouts == null) return false;
  return Object.entries(currentLayouts).some(
    ([windowId, layout]) => layout.join(',') !== activeStepLayouts[windowId]
  );
}

/**
 * Canonical form of the inputs the series classifier is given for one viewport.
 *
 * Mirrors `SeriesClassifierInput`'s query variables exactly: the viewport's own description, plus the
 * protocol's study description but only for a current-study viewport, since that is the only case
 * where it is sent. Anything that changes either input changes this string, which is what makes
 * staleness detectable without every writer having to declare it (VX-4977).
 */
export function classifierInputSignature(
  seriesDescription: string,
  appliedStudyDescription: string
): string {
  // NUL separates the two so no pair of inputs can collide by concatenation.
  //
  // The series a viewport holds is deliberately not part of this, even though it changes what the
  // classifier is asked (VX-4970). Which series gets asked about is only partly the viewport's to
  // say: when the form records none, the component falls back to whatever the viewer has hung
  // there, and that can change without the reader doing anything. Folding it in here would make
  // the gate depend on that, and would disagree with itself whenever the fallback applied. A drop
  // that swaps the series clears the criteria and these signatures instead, which is the same
  // protection placed where the change is actually known.
  return `${seriesDescription.trim()}\u0000${appliedStudyDescription.trim()}`;
}

/**
 * The signature of the inputs the classifier would be given for this viewport right now.
 *
 * Applies the one rule that decides whether the study description is part of the question: it is sent
 * only for a current-study viewport, so a prior-study viewport's classification does not go stale when
 * the protocol's study description is edited.
 */
export function viewportClassifierSignature(
  viewport: Pick<
    HangingProtocolFormState['viewports'][string],
    'seriesCriteriaSourceText' | 'comparisonIndex'
  >,
  studyDescription: string
): string {
  return classifierInputSignature(
    viewport.seriesCriteriaSourceText,
    viewport.comparisonIndex === null ? studyDescription : ''
  );
}

/**
 * Whether a viewport's classification is settled, and if not, why not.
 *
 * Derived rather than stored. The previous version of this kept a written `classificationState` on
 * each viewport, which meant every path that touched a viewport had to remember to invalidate it, and
 * six of them did not: typing a description, changing the comparison index, editing the protocol's
 * study description, two seeding paths and the cross-window merge. Comparing signatures moves that
 * from a rule people follow to a fact about the data.
 *
 * RESOLVED includes empty criteria: a description the classifier maps to nothing, and a slot the
 * reader cleared, are both real answers and both save fine. Only PENDING and FAILED block a save.
 */
export function viewportClassificationState(
  viewport: HangingProtocolFormState['viewports'][string],
  studyDescription: string
): ClassificationState {
  if (viewport.isClearViewport) return CLASSIFICATION_STATE.RESOLVED;
  // Nothing to classify, so nothing to wait for.
  if (viewport.seriesCriteriaSourceText.trim() === '') return CLASSIFICATION_STATE.RESOLVED;

  const liveSignature = viewportClassifierSignature(viewport, studyDescription);
  if (viewport.failedSignature === liveSignature) return CLASSIFICATION_STATE.FAILED;
  if (viewport.criteriaSignature === liveSignature) return CLASSIFICATION_STATE.RESOLVED;
  return CLASSIFICATION_STATE.PENDING;
}

export function unresolvedViewportIds(
  formState: HangingProtocolFormState,
  layouts: Nullable<Layouts>,
  /**
   * Windows the reader can currently see. A viewport in a window that is closed is left alone:
   * there is no classifier input mounted for it, so it can never settle, and naming it in the banner
   * asks for something the reader cannot do from here. Omit to consider every window, which callers
   * should only do when they genuinely mean every window rather than when they do not know.
   */
  visibleWindowIds?: Nullable<ReadonlySet<string>>
): string[] {
  // isViewportInLayoutBounds is the shared bounds check, and it speaks the backend's "2,2" strings
  // rather than the viewer's [rows, cols]. Converting here keeps one bounds implementation.
  const layoutStrings = Object.fromEntries(
    Object.entries(layouts ?? {}).map(([windowId, layout]) => [windowId, layout.join(',')])
  );
  // Bounds are judged per window, because the layouts map is routinely partial. The override layer
  // is written one window at a time (`setUserLayoutForWindow` preserves the case's other windows by
  // design), and `useLayouts` fills the gaps with DEFAULT_LAYOUT at *read* time, so a map holding
  // only window 0 while window 1 renders at its default is the normal state rather than an edge.
  // Asking "does any window have bounds" therefore turned the filter on for every window as soon as
  // one of them had a layout, and `isViewportInLayoutBounds` answers false for a window it has no
  // key for, so a dual-monitor session lost the check on its second window while the save kept
  // sending that window's viewports.
  //
  // A window with no layout is left unbounded instead. That keeps the reason the global form
  // existed: `hangingProtocolLayoutsState` defaults to null and is restored asynchronously, so "no
  // layouts" is a real state and not only a bug, and filtering by bounds we do not have would drop
  // every viewport and quietly turn this check off. With no bounds for a window, check all of its
  // viewports: the step flush falls back to the step's own layouts in the same situation, so those
  // viewports do get sent.
  const windowHasBounds = (windowId: string): boolean => layoutStrings[windowId] != null;
  return Object.entries(formState.viewports)
    .filter(([viewportId, viewport]) => {
      const { windowId } = viewportIdToConfig(viewportId);
      if (visibleWindowIds != null && !visibleWindowIds.has(windowId)) return false;
      if (windowHasBounds(windowId) && !isViewportInLayoutBounds(viewportId, layoutStrings))
        return false;
      return (
        viewportClassificationState(viewport, formState.studyCriteriaSourceText ?? '') !==
        CLASSIFICATION_STATE.RESOLVED
      );
    })
    .map(([viewportId]) => viewportId)
    .sort((a, b) => compareViewportIds(a, b));
}

/** A viewport belonging to one of the protocol's steps, named by the step it lives in. */
export type StepViewportRef = { stepIndex: number; viewportId: string };

/**
 * Viewports in the protocol's other steps that would be saved with a series description and nothing
 * classified behind it.
 *
 * The form check above cannot see these. Form state holds one step at a time, so on a stepped
 * protocol it describes the step the reader is looking at and nothing else, and the signatures it
 * judges by do not survive the trip into a step: `formViewportToStepViewport` drops them, and
 * `ViewportConfigurationEntry` has no field to keep them in. What a step entry does record is the
 * pair that matters -- a description, and the criteria derived from it -- so the same question is
 * asked here of the shape being sent: a description with no criteria beside it is the VX-4977 state.
 *
 * Read the step's own layouts, because a step carries the grid it was configured against and the
 * save only sends the viewports inside it. Windows are treated exactly as the form check treats
 * them, for the same two reasons: a partial layouts map is normal, and a viewport in a window the
 * reader cannot see could never be settled from here.
 *
 * Call this *after* `fillMissingSeriesCriteria`. A viewport holding a series gets its criteria from
 * the series record rather than from the classifier, so asking first would refuse saves that were
 * about to go out complete.
 *
 * The current step is skipped: the flush has just written form state into it, so the form check owns
 * it and would otherwise name it twice.
 */
export function unresolvedStepViewports(
  steps: Nullable<StepConfiguration[]>,
  options: {
    /** The step the form is showing, which the form check above already covers. */
    currentStepIndex: number;
    /** Windows the reader can currently see; omit to consider every window. */
    visibleWindowIds?: Nullable<ReadonlySet<string>>;
  }
): StepViewportRef[] {
  if (steps == null) return [];
  const { currentStepIndex, visibleWindowIds } = options;
  const unresolved: StepViewportRef[] = [];

  steps.forEach((step, stepIndex) => {
    if (stepIndex === currentStepIndex) return;
    for (const [viewportId, entry] of Object.entries(step.viewports)) {
      const { windowId } = viewportIdToConfig(viewportId);
      if (visibleWindowIds != null && !visibleWindowIds.has(windowId)) continue;
      if (step.layouts[windowId] != null && !isViewportInLayoutBounds(viewportId, step.layouts))
        continue;
      if (entry.is_clear === true) continue;
      if ((entry.series_criteria_source_text ?? '').trim() === '') continue;
      if (entry.criteria != null && entry.criteria.length > 0) continue;
      unresolved.push({ stepIndex, viewportId });
    }
  });

  return unresolved.sort(
    (a, b) => a.stepIndex - b.stepIndex || compareViewportIds(a.viewportId, b.viewportId)
  );
}

/** Order viewport ids by window, then row, then column, so a 10th column sorts after a 2nd. */
function compareViewportIds(a: string, b: string): number {
  const left = viewportIdToConfig(a);
  const right = viewportIdToConfig(b);
  return (
    Number(left.windowId) - Number(right.windowId) ||
    left.rowIndex - right.rowIndex ||
    left.colIndex - right.colIndex
  );
}

/**
 * Human-readable viewport position, e.g. "Viewport-1-0-2" becomes "viewer 2, row 1, column 3".
 *
 * Every number the reader sees counts from one, the viewer included. The window id in a viewport id
 * counts from zero, and the app has always shown that same window to the reader as "Viewer 1" in the
 * navigation, so printing it raw named a viewer that does not exist on their screen.
 *
 * Delegates the parse to `viewportIdToConfig` rather than repeating the id format here: two copies of
 * that pattern are two chances to disagree when the layout model changes.
 */
export function describeViewportPosition(viewportId: string): string {
  const { windowId, rowIndex, colIndex } = viewportIdToConfig(viewportId);
  return `viewer ${Number(windowId) + 1}, row ${rowIndex + 1}, column ${colIndex + 1}`;
}

/**
 * Same position, prefixed with the step it is in, counting from one like every other number the
 * reader sees.
 */
export function describeStepViewportPosition({ stepIndex, viewportId }: StepViewportRef): string {
  return `step ${stepIndex + 1}, ${describeViewportPosition(viewportId)}`;
}
